import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useEditContext } from "../../hooks/useEditContext"
import FeaturesFormSwitcher from "../../components/forms/FeaturesFormSwitcher";
import { FeatureEnum, FeatureType, toFeatureEnumOf } from "../../types/feature";
import { getFeatureLabel } from "../../utils/features";
import useFeatures from "../../hooks/useFeatures";
import useSaveFeature from "../../hooks/useSaveFeature";
import Alert from "../../components/alert/Alert";
import { Helmet } from "react-helmet-async";
import PageSectionHeader from "../../components/PageSectionHeader";

export default function EditOject () {
  const intl = useIntl();
  const {object} = useEditContext();
  const [objName, setObjName] = useState<string>('');
  const [selectedFeature, selectFeature] = useState<FeatureEnum>(FeatureEnum.Human);
  const [features, setfeatures] = useState<FeatureType[]|null>(null);
  const [featureToEdit, setFeatureToEdit] = useState<FeatureType|null>(null);
  const [error, setError] = useState<Error|null>(null);
  const {getFeatures, data, objName : objNameRequest, error: fetchingError} = useFeatures();
  const {saveFeature, saveResponse, error: savingError} = useSaveFeature();
  
  useEffect(() => {
    if (object) {
      getFeatures(object.id);
      if (object.activeFeature) {
       selectFeature(object.activeFeature.type) 
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [object]);

  useEffect(() => {
    if (data) {
      setfeatures(data);
      setObjName(objNameRequest);
    }
  }, [data, objNameRequest])

  useEffect(() => {
    if (features && features.length > 0) {
      const findFeature = features.find(feat => feat.type === selectedFeature);
      setFeatureToEdit(findFeature ?? null);
    } else {
      setFeatureToEdit(null);
    }
  }, [features, selectedFeature])

  useEffect(() => {
    if (savingError || fetchingError) {
      setError(savingError ?? fetchingError)
    }
    setError(null);
  }, [fetchingError, savingError])

  function handleFeatureChange(value: string) {
    const featureSelected = toFeatureEnumOf(value)!;
    selectFeature(featureSelected);
  }

  function onFormSwitcherChange(value: unknown) {    
    if (value && object && selectedFeature) {
      const {id} = object;
      saveFeature(id, selectedFeature, value, objName);
    }
  }

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage(
            { id: "equipment.edit.documentTitle" },
            { appName: import.meta.env.VITE_APP_NAME }
          )}
        </title>
      </Helmet>
      <PageSectionHeader className="mb-16">
        <h2 className="text-xl font-normal text-shadow">
          {intl.formatMessage({ id: "equipment.edit.heading" })}
        </h2>
      </PageSectionHeader>
      <div className="mb-5">
        <label className="text-shadow text-md">
          {intl.formatMessage({ id: "equipment.edit.changeFeature" })}
        </label>
        <select className="ml-5 cursor-pointer" name="selectedFeature"
          value={selectedFeature || undefined} onChange={(e) => handleFeatureChange(e.target.value)}>
          {
            Object.values(FeatureEnum).map(value => {
              const title = getFeatureLabel(intl, value)!;
              return (
                <option className="cursor-pointer" key={value} value={value}>{title}</option>
              )
            })
          }
        </select>
      </div>

      {/* Form switcher */}
      { selectedFeature && (
        <FeaturesFormSwitcher
          featureType={selectedFeature}
          data={featureToEdit?.data ?? null}
          objName={objName}
          updateObjName={setObjName}
          valueFormChange={onFormSwitcherChange}/>
      )}
        
      { error && <Alert message={error.message} type="error" className="w-fit mb-5" /> }
      { saveResponse && (
        <Alert
          message={intl.formatMessage({ id: "equipment.edit.saved" })}
          type="success"
          className="w-fit mb-5"
        />
      )}
    </>
  )
}
